import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData, isSameData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { PaymentProvider } from './payment-provider.js';
import { PaymentStatus } from './payment-status.js';
import { VersionRange } from '../core/version-range.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { InvoiceType } from './invoicing/invoice-type.js';
import { LegacyInvoiceDetails } from './invoicing/legacy-invoice-details.js';
import { InvoiceProvider } from './invoicing/invoice-provider.js';
import { Dayjs } from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { OnlineInvoicingDetails } from './invoicing/online-invoicing-details.js';
import { Invoice } from './invoicing/invoice.js';
import { MigrationSafe } from '../core/migration-safe.js';
import { JSONUtils } from '../../utility/json-utils.js';

export class OnlinePaymentDetails extends DataModel {
  static readonly TYPE_ID: string = '5358056c948b499bab9cbde5ed251e7d';
  static readonly SCHEMA_VERSION: number = 12;

  private constructor(
    public readonly invoiceType: InvoiceType,
    public readonly status: PaymentStatus,
    public readonly provider: PaymentProvider | null,
    public readonly paymentRequestId: string | null,
    public readonly paymentId: string | null,
    public readonly paymentDate: Dayjs | null,
    public readonly checkoutSessionId: string | null,
    public readonly invoicingDetails: OnlineInvoicingDetails | null,
    public readonly applicationFeeTransactionId: string | null,
  ) {
    super();
  }

  get $typeId() {
    return OnlinePaymentDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(11, OnlinePaymentDetails.SCHEMA_VERSION);
  }

  get isSuccess() {
    return this.status === PaymentStatus.Succeeded;
  }

  private static create(options: {
    invoiceType: InvoiceType,
    status: PaymentStatus,
    provider?: PaymentProvider | null,
    paymentRequestId?: string | null,
    paymentId?: string | null,
    paymentDate?: Dayjs | null,
    checkoutSessionId?: string | null,
    invoicingDetails?: OnlineInvoicingDetails | null,
    applicationFeeTransactionId?: string | null,
  }): OnlinePaymentDetails {
    return new OnlinePaymentDetails(
        options.invoiceType,
        options.status,
        options.provider ?? null,
        options.paymentRequestId ?? null,
        options.paymentId ?? null,
        options.paymentDate ?? null,
        options.checkoutSessionId ?? null,
        options.invoicingDetails ?? null,
        options.applicationFeeTransactionId ?? null,
    );
  }

  static checkoutSession(options: {
    invoiceType: InvoiceType,
    status: PaymentStatus,
    provider: PaymentProvider,
    paymentRequestId: string,
    paymentId: string,
    checkoutSessionId: string,
    applicationFeeTransactionId: string | null,
  }): OnlinePaymentDetails {
    return OnlinePaymentDetails.create(options);
  }

  static prepared(invoiceType: InvoiceType): OnlinePaymentDetails {
    return OnlinePaymentDetails.create({ invoiceType, status: PaymentStatus.Prepared });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlinePaymentDetails {
    return new OnlinePaymentDetails(
        toEnumFromString(MapUtils.getString(map, 'invoiceType'), InvoiceType),
        toEnumFromString(MapUtils.getString(map, 'status'), PaymentStatus),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'provider'), PaymentProvider),
        MapUtils.getStringOrNull(map, 'paymentRequestId'),
        MapUtils.getStringOrNull(map, 'paymentId'),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'paymentDate')),
        MapUtils.getStringOrNull(map, 'checkoutSessionId'),
        context.fromDataOrNull<OnlineInvoicingDetails>(MapUtils.getRecordOrNull(map, 'invoicingDetails')),
        MapUtils.getStringOrNull(map, 'applicationFeeTransactionId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'invoiceType': fromEnumAsString(this.invoiceType),
      'status': fromEnumAsString(this.status),
      'provider': fromEnumAsStringOrNull(this.provider),
      'paymentRequestId': this.paymentRequestId,
      'paymentId': this.paymentId,
      'paymentDate': context.encodeDateOrNull(this.paymentDate),
      'checkoutSessionId': this.checkoutSessionId,
      'invoicingDetails': context.toDataOrNull(this.invoicingDetails),
      'applicationFeeTransactionId': this.applicationFeeTransactionId,
    };
  }

  clone(): this {
    return new OnlinePaymentDetails(
        this.invoiceType,
        this.status,
        this.provider,
        this.paymentRequestId,
        this.paymentId,
        TimeZone.cloneDateOrNull(this.paymentDate),
        this.checkoutSessionId,
        this.invoicingDetails?.clone() ?? null,
        this.applicationFeeTransactionId,
    ) as this;
  }

  isSame(other: OnlinePaymentDetails): boolean {
    if(this === other) {
      return true;
    }

    return this.invoiceType === other.invoiceType &&
      this.status === other.status &&
      this.provider === other.provider &&
      this.paymentId === other.paymentId &&
      this.checkoutSessionId === other.checkoutSessionId &&
      this.paymentRequestId === other.paymentRequestId &&
      this.applicationFeeTransactionId === other.applicationFeeTransactionId &&
      isSameData(other.invoicingDetails, this.invoicingDetails);
  }

  copyWith(data: Partial<OnlinePaymentDetails>): OnlinePaymentDetails {
    return new OnlinePaymentDetails(
        data.invoiceType ?? this.invoiceType,
        data.status ?? this.status,
        data.provider ?? this.provider,
        data.paymentRequestId ?? this.paymentRequestId,
        data.paymentId ?? this.paymentId,
        data.paymentDate ?? TimeZone.cloneDateOrNull(this.paymentDate),
        data.checkoutSessionId ?? this.checkoutSessionId,
        data.invoicingDetails ?? (this.invoicingDetails?.clone() ?? null),
        data.applicationFeeTransactionId ?? this.applicationFeeTransactionId,
    );
  }
}

export class OnlinePaymentDetailsBuilder extends DataModelBuilder<OnlinePaymentDetails> {
  build(context: SerializationContext, map: SerializedData): OnlinePaymentDetails {
    return OnlinePaymentDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OnlinePaymentDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlinePaymentDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    const migrationSafeInvoiceCreationDate = MigrationSafe.decode(
        MapUtils.getRaw(map, 'paymentDate'),
        (data) => context.decodeDate(JSONUtils.decodeNumber(data))
    );

    let invoiceCreationDate = migrationSafeInvoiceCreationDate.isSome ? migrationSafeInvoiceCreationDate.value : null;
    if(invoiceCreationDate == null) {
      const orderCreationDate = context.storage.get<Dayjs>('orderCreationDate');
      const lastUpdateDate = context.decodeDate(MapUtils.getInteger(map, '$last_update'));

      // Not ideal to set the invoice creation date to last update time but it's the best we can do :(
      invoiceCreationDate = orderCreationDate ?? lastUpdateDate;
    }

    context.storage.set('invoiceCreationDate', invoiceCreationDate);

    // In this version the [paymentRequestId] field was added.
    if(version < 2) {
      MapUtils.set(map, 'paymentRequestId', null);
      version = 2;
    }

    // In this version the [checkoutSessionId] field was added.
    if(version < 3) {
      MapUtils.set(map, 'checkoutSessionId', null);
      version = 3;
    }

    // In this version the invoicing related fields were added.
    if(version < 4) {
      MapUtils.set(map, 'invoiceProvider', null);
      MapUtils.set(map, 'invoiceId', null);
      MapUtils.set(map, 'invoiceDownloadUrl', null);
      version = 4;
    }

    // In this version the [invoiceType] field was added.
    if(version < 5) {
      MapUtils.set(map, 'invoiceType', fromEnumAsString(InvoiceType.PhysicalPerson));
      version = 5;
    }

    // In this version the [invoices] field was added.
    if(version < 6) {
      const invoiceProvider = toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'invoiceProvider'), InvoiceProvider);
      const invoiceId = MapUtils.getOrNull<string>(map, 'invoiceId');
      const invoiceDownloadUrl = MapUtils.getOrNull<string>(map, 'invoiceDownloadUrl');
      const invoices: LegacyInvoiceDetails[] = [];

      if(invoiceProvider != null && invoiceId != null && invoiceDownloadUrl != null) {
        invoices.push(LegacyInvoiceDetails.create({
          provider: invoiceProvider,
          uniqueId: invoiceId,
          name: invoiceId,
          downloadUrl: invoiceDownloadUrl,
        }));
      }

      map['invoices'] = invoices.map((e) => context.toData(e));
      version = 6;
    }

    // In this version the [requiresApplicationFeeInvoice] field was added.
    if(version < 7) {
      MapUtils.set(map, 'requiresApplicationFeeInvoice', false);
      version = 7;
    }

    // Version 8:
    // - add `paymentDate` field of type [Dayjs | null].
    if(version < 8) {
      map['paymentDate'] = null;
      version = 8;
    }

    // Version 9:
    // - add `invoicingDetails` field of type [OnlineInvoicingDetails].
    if(version < 9) {
      const legacyInvoices = MapUtils.getList<LegacyInvoiceDetails>(map, 'invoices', (value) => {
        return context.fromData<LegacyInvoiceDetails>(value as SerializedData);
      });

      let invoicingDetails: OnlineInvoicingDetails | null = null;
      if(legacyInvoices.length > 0) {
        invoicingDetails = OnlineInvoicingDetails.fromLegacy({
          orderInvoice: Invoice.fromLegacy(legacyInvoices[0], invoiceCreationDate),
          applicationFeeInvoice: legacyInvoices.length > 1 ? Invoice.fromLegacy(legacyInvoices[1], invoiceCreationDate) : null,
        });
      }

      map['invoicingDetails'] = context.toDataOrNull(invoicingDetails);
      version = 9;
    }

    // sadly we didn't keep track of what this version did at the time :(
    if(version < 10) {
      version = 10;
    }

    // In this version `requiresApplicationFeeInvoice` of type `boolean` was replaced with applicationFeeTransactionId of type `string | null`
    if(version < 11) {
      const requiresApplicationFeeInvoice = MapUtils.getBoolean(map, 'requiresApplicationFeeInvoice');
      const paymentRequestId = MapUtils.getStringOrNull(map, 'paymentRequestId');

      map['applicationFeeTransactionId'] = paymentRequestId != null ?
        (requiresApplicationFeeInvoice ? `fee.${paymentRequestId}` : null) : null;

      version = 11;
    }

    // change type of [paymentDate] from [MigrationSafe<Dayjs>] to [Dayjs]
    if(version < 12) {
      const paymentDate = MigrationSafe.decode(
          MapUtils.getRaw(map, 'paymentDate'),
          (data) => context.decodeDate(JSONUtils.decodeNumber(data))
      );

      map['paymentDate'] = context.encodeDateOrNull(paymentDate.isSome ? paymentDate.value : null);
      version = 12;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
